// src/pages/misc/onboarding/OnboardingAccountPage.tsx
// Sign-up sits just before checkout. It looks like the normal sign-up card,
// with the price in its own card above. Nothing is created on the server here.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, ChevronRight, Eye, EyeOff, Lock, Mail, User, X } from 'lucide-react';
import { useTranslation } from '../../../contexts/languages/LanguageContext';
import { isStructureDraftReady, loadStructureDraft } from './structureDraft';
import { publicJourney } from './continuousPath';
import OnboardingFrame from './OnboardingFrame';
import { checkPerSeatApplication, holdPassword, type ApiRefusal } from './perSeatApi';
import { PRICES_FAILED_LINE, usePriceBook } from './usePriceBook';
import { loadOrgSetup } from '../../settings/create-organization/orgSetup';
import { formatMxn, orderQuote } from '../../settings/create-organization/orderQuote';
import './builder.css';
import '../../settings/create-organization/orgFlow.css';
import '../../auth/auth.css';

// [HANDOFF] sessionStorage key. Holds ONLY { first, last, email }. It is cleared when the browser tab closes.
export const PERSON_KEY = 'ventaje_ob_person';

// Reads the saved person back (used here to refill the form and on the checkout page to show who is paying).
export function loadOnboardingPerson(): { first: string; last: string; email: string } {
  try {
    const raw = sessionStorage.getItem(PERSON_KEY);
    if (!raw) return { first: '', last: '', email: '' };
    const parsed = JSON.parse(raw) as { first?: string; last?: string; email?: string };
    return {
      first: String(parsed.first || ''),
      last: String(parsed.last || ''),
      email: String(parsed.email || ''),
    };
  } catch {
    return { first: '', last: '', email: '' };
  }
}

// [SIGNUP-TOGETHER] Sign-up of the live, continuous onboarding: /onboarding/cuenta (step 4 of the public tail:
// plan -> extensiones -> cuenta -> checkout). Not to be confused with:
//   [SIGNUP-SPLIT] /signup?listo=1  (SignupPage.tsx, the older split flow, and the only one that really creates a user today)
//   [SIGNUP-PLAIN] /signup          (SignupPage.tsx without a saved structure)
//   [SIGNUP-INVITE] /join/:token    (JoinInvitePage.tsx, a teammate invited by an admin)
// [HANDOFF] THE SERVER'S FOUR PASSWORD RULES (edge_functions/submit-per-seat-onboarding.ts passwordWeakness, in its order): the checklist
// under the field and the submit's first unmet line. Its fifth rule (a common password) is the server's alone: P2's refusal shows it.
const PASSWORD_RULES: { key: string; met: (password: string) => boolean }[] = [
  { key: 'settings.createOrganization.passwordRule.length', met: (p) => p.length >= 8 },
  { key: 'settings.createOrganization.passwordRule.upper', met: (p) => /[A-Z]/.test(p) },
  { key: 'settings.createOrganization.passwordRule.digit', met: (p) => /[0-9]/.test(p) },
  { key: 'settings.createOrganization.passwordRule.special', met: (p) => /[!@#$%^&*()_+=[\]{}|;:,.<>?-]/.test(p) },
];

// The log-in page an e-mail that already has an account is sent to (S-12, D5).
const LOGIN_URL = 'https://app.ventaje.com/login';

// The line a refusal's message lands on, by the field the server names; none -> the form's line above the button.
const REFUSAL_LINES: Record<string, string> = { email: 'email', name: 'account' };

// A refusal's message is the server's own (Spanish, S-6) unless perSeatApi made it: NETWORK_ERROR, ANSWER_MALFORMED, and the
// `<proxy> answered <status>` line of a status the server sent no words with. Null -> the page's own accountCheckFailed line.
function serverMessage(refusal: ApiRefusal): string | null {
  if (refusal.code === 'NETWORK_ERROR' || refusal.code === 'ANSWER_MALFORMED') return null;
  return /^\/api\/\S+ answered \d+$/.test(refusal.message) ? null : refusal.message;
}

// [HANDOFF] THIS SCREEN CREATES NOTHING ON THE SERVER. It validates, asks the server whether the account name and the e-mail are still
// free (P1), stores name + email in sessionStorage, and goes to checkout.
// [HANDOFF] THE PASSWORD is held IN MEMORY by perSeatApi's holdPassword until checkout's submit reads it - never in localStorage/sessionStorage.
// [I18N] Most text uses t('settings.createOrganization.*'); the placeholders "Laura", "Méndez", "laura@empresa.com" are typed in the code.
const OnboardingAccountPage: React.FC = () => {
  const { t, language } = useTranslation();
  const navigate = useNavigate();
  const draft = loadStructureDraft();
  // [PRICING] The price card's figures come from the book (S-9), as checkout's will: null until the book is read or when a row it needs is missing.
  const { book, failed, retry } = usePriceBook();
  const quote = book === null ? null : orderQuote(book, draft, loadOrgSetup());
  const quoteMissing = book !== null && quote === null;
  // [ANIMATION] Progress for the left thread: step = (builder screens) + index of 'cuenta' in PUBLIC_TAIL (see continuousPath.ts).
  const progress = publicJourney(draft.path, 'cuenta');
  const saved = loadOnboardingPerson();
  const [first, setFirst] = useState(saved.first);
  const [last, setLast] = useState(saved.last);
  const [email, setEmail] = useState(saved.email);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // The check (P1) is running: the gold button waits. The e-mail line offers the log-in page when the e-mail is taken.
  const [checking, setChecking] = useState(false);
  const [loginOffered, setLoginOffered] = useState(false);

  // [HANDOFF] Guard: no finished structure (company name + path 2 or 3) -> back to the builder.
  useEffect(() => {
    if (!isStructureDraftReady(draft)) navigate('/onboarding', { replace: true });
  }, [draft, navigate]);

  const money = (n: number) => formatMxn(n, language);
  const prepaid = quote !== null && quote.months > 1;
  const monthsText = quote === null ? '' : `${quote.months} ${t('settings.createOrganization.billMonths')}`;
  const savedTitle = draft.name
    ? `${draft.name} ${t('settings.createOrganization.accountSaved')}`
    : t('settings.createOrganization.accountSavedPlain');

  // Validation rules: both names required, email must look like x@y.z, the password meets the server's four rules, confirm must match.
  // [HANDOFF] Then P1 (D1 THE CHECK): the server says whether the account's name (the builder's draft.name) and the e-mail are free.
  const submit = async () => {
    const next: Record<string, string> = {};
    if (!first.trim() || !last.trim()) next.name = t('settings.createOrganization.accountNameRequired');
    if (!/\S+@\S+\.\S+/.test(email.trim())) next.email = t('settings.createOrganization.accountEmailRequired');
    const unmet = PASSWORD_RULES.find((rule) => !rule.met(password));
    if (!password) next.password = t('settings.createOrganization.accountPasswordRequired');
    else if (unmet) next.password = t(unmet.key);
    if (confirm !== password) next.confirm = t('settings.createOrganization.accountMismatch');
    setErrors(next);
    setLoginOffered(false);
    if (Object.keys(next).length) return;
    setChecking(true);
    const check = await checkPerSeatApplication(draft.name, email.trim());
    setChecking(false);
    if (!check.ok) {
      const said = serverMessage(check.refusal);
      const line = said === null ? 'form' : REFUSAL_LINES[check.refusal.field ?? ''] ?? 'form';
      setErrors({ [line]: said ?? t('settings.createOrganization.accountCheckFailed') });
      return;
    }
    if (!check.value.nameAvailable || !check.value.emailAvailable) {
      const taken: Record<string, string> = {};
      if (!check.value.nameAvailable) taken.account = t('settings.createOrganization.accountNameTaken');
      if (!check.value.emailAvailable) taken.email = t('settings.createOrganization.accountEmailTaken');
      setErrors(taken);
      setLoginOffered(!check.value.emailAvailable);
      return;
    }
    try {
      sessionStorage.setItem(PERSON_KEY, JSON.stringify({
        first: first.trim(),
        last: last.trim(),
        email: email.trim(),
      }));
    } catch {
      /* ignore */
    }
    holdPassword(password);
    navigate('/onboarding/checkout');
  };

  return (
    <OnboardingFrame
      step={progress.step}
      total={progress.total}
      band="mid"
      foot={(
        <div className="org-float">
          <button type="button" className="ob-back" onClick={() => navigate('/onboarding/extensiones')}>
            {t('settings.createOrganization.back')}
          </button>
          <button
            type="button"
            className="btn-gold inline-flex items-center justify-center gap-1 min-h-[48px] px-6 rounded-xl text-sm font-bold"
            onClick={submit}
            disabled={quote === null || checking}
          >
            {t('settings.createOrganization.continueAccount')}
            <ChevronRight size={16} />
          </button>
        </div>
      )}
    >
      {/* [SPACING] Two glass cards STACKED in .ob-signup (builder.css): column, 28px gap, max 460px wide, 7vh margin-top, 48px bottom padding. Price card on top, sign-up form below. */}
      {/* [PRICING] Same numbers as checkout (orderQuote). Monthly: quote.total "al mes", plan = seats x perSeat, extras per month.
          6 months / annual: quote.dueNow "a pagar hoy", plan = seats x discounted perSeat x months (= planDue),
          extras = extras x months (= extrasDue). Example, 11 seats Base on 6 months: 11 x $249 x 6 meses = $16,434. */}
      <div className="ob-signup">
        <section className="auth-card ob-signup-price">
          <h2>{savedTitle}</h2>
          {errors.account ? <p className="auth-err"><AlertTriangle size={12} />{errors.account}</p> : null}
          {/* [PRICING] THE FAILED READ (S-9): the one line in the card when the book failed or a row the quote needs is missing; it is the retry. */}
          {failed || quoteMissing ? (
            <button type="button" className="ob-tag underline" onClick={retry}>
              {PRICES_FAILED_LINE}
            </button>
          ) : null}
          {quote === null ? null : (
            <>
          <p className="ob-total">
            {money(prepaid ? quote.dueNow : quote.total)}
            <span>{t(prepaid ? 'settings.createOrganization.dueNow' : 'settings.createOrganization.perMonth')}</span>
          </p>
          <div className="ob-stat">
            <span>{t(`settings.upgrade.plans.${quote.planId}.name`)}</span>
            <b className="tabular-nums">
              {quote.seats} × {money(quote.perSeat)}
              {prepaid ? ` × ${monthsText} = ${money(quote.planDue)}` : ''}
            </b>
          </div>
          <div className="ob-stat">
            <span>{t('settings.createOrganization.billExtras')}</span>
            <b className="tabular-nums">
              {prepaid ? `${money(quote.extras)} × ${monthsText} = ${money(quote.extrasDue)}` : money(quote.extras)}
            </b>
          </div>
            </>
          )}
        </section>

        <section className="auth-card">
          <h2>{t('settings.createOrganization.accountTitle')}</h2>
          <p className="auth-lead">{t('settings.createOrganization.accountSubtitle')}</p>

          <div className="auth-names">
            <div className="auth-field">
              <label htmlFor="ob-first">{t('settings.createOrganization.accountFirst')}</label>
              <div className="auth-input">
                <User size={16} />
                <input id="ob-first" className={errors.name ? 'warn' : ''} value={first} placeholder="Laura" onChange={(e) => setFirst(e.target.value)} />
              </div>
            </div>
            <div className="auth-field">
              <label htmlFor="ob-last">{t('settings.createOrganization.accountLast')}</label>
              <div className="auth-input">
                <User size={16} />
                <input id="ob-last" className={errors.name ? 'warn' : ''} value={last} placeholder="Méndez" onChange={(e) => setLast(e.target.value)} />
              </div>
            </div>
          </div>
          {errors.name ? <p className="auth-err"><AlertTriangle size={12} />{errors.name}</p> : null}

          <div className="auth-field">
            <label htmlFor="ob-email">{t('settings.createOrganization.accountEmail')}</label>
            <div className="auth-input">
              <Mail size={16} />
              <input id="ob-email" type="email" className={errors.email ? 'warn' : ''} value={email} placeholder="laura@empresa.com" onChange={(e) => setEmail(e.target.value)} />
            </div>
            {errors.email ? (
              <p className="auth-err">
                <AlertTriangle size={12} />
                {errors.email}
                {loginOffered ? <a className="ml-1 underline" href={LOGIN_URL}>{t('settings.createOrganization.accountEmailLogin')}</a> : null}
              </p>
            ) : null}
          </div>

          <div className="auth-field">
            <label htmlFor="ob-pass">{t('settings.createOrganization.accountPassword')}</label>
            <div className="auth-input">
              <Lock size={16} />
              <input
                id="ob-pass"
                type={showPassword ? 'text' : 'password'}
                className={errors.password ? 'warn' : ''}
                value={password}
                placeholder={language === 'es' ? 'Mínimo 8 caracteres' : 'At least 8 characters'}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button type="button" className="auth-eye" onClick={() => setShowPassword((v) => !v)} aria-label={showPassword ? 'Hide' : 'Show'}>
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <ul className="mt-2 space-y-1 text-xs">
              {PASSWORD_RULES.map((rule) => (
                <li key={rule.key} className={rule.met(password) ? 'flex items-center gap-1' : 'flex items-center gap-1 opacity-60'}>
                  {rule.met(password) ? <Check size={12} /> : <X size={12} />}
                  {t(rule.key)}
                </li>
              ))}
            </ul>
            {errors.password ? <p className="auth-err"><AlertTriangle size={12} />{errors.password}</p> : null}
          </div>

          <div className="auth-field">
            <label htmlFor="ob-confirm">{t('settings.createOrganization.accountConfirm')}</label>
            <div className="auth-input">
              <Lock size={16} />
              <input
                id="ob-confirm"
                type={showPassword ? 'text' : 'password'}
                className={errors.confirm ? 'warn' : ''}
                value={confirm}
                placeholder={language === 'es' ? 'Otra vez' : 'Again'}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            {errors.confirm ? <p className="auth-err"><AlertTriangle size={12} />{errors.confirm}</p> : null}
          </div>
          {errors.form ? <p className="auth-err"><AlertTriangle size={12} />{errors.form}</p> : null}
        </section>
      </div>
    </OnboardingFrame>
  );
};

export default OnboardingAccountPage;
